import type {
  CustomerManagerUsageOverviewResponse,
  CustomerManagerUsageTotals,
} from '@nadoutong/contracts';
import { Download, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  crmUsageOverviewCsvUrl,
  useCrmUsageOverview,
  type CrmUsageOverviewQuery,
  type OpsUsageWindow,
} from '../api/opsUsage.ts';
import { usePricing } from '../api/pricing.ts';
import {
  formatLocalTimestamp,
  formatUsdMicrosOrCents,
  formatUsdMicrosOrCentsAsCnyDisplay,
  formatUsdMicrosOrCentsAsCnyMetered,
} from '../components/formatters.ts';
import { LocalDateTimeInput } from '../components/LocalDateTimeInput.tsx';
import { useT } from '../i18n/index.ts';
import {
  Button,
  KpiCard,
  PageHeader,
  Panel,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
  buttonClass,
} from '../ui/index.ts';

/**
 * /crm/usage — scoped usage overview for sales sessions (epic #1035
 * E6-D4, #1068).
 *
 * Renders the SALES projection (`CustomerManagerUsageOverviewResponse`):
 * requests / tokens / tenant-charge by model and by SKU. The server
 * forces the book scope for CM/lead sessions (client params can never
 * widen it) and strips every procurement/upstream/profit field; admin
 * sessions opt into the same projection via `view=crm`, so this page
 * renders one contract for everyone. The CSV export goes through the
 * same server-side projection.
 */

const TOKEN_FORMATTER = new Intl.NumberFormat('en-US');

function formatTokens(n: number): string {
  return TOKEN_FORMATTER.format(n);
}

function formatTokenPair(a: number, b: number): string {
  return `${formatTokens(a)} / ${formatTokens(b)}`;
}

function parseWindow(raw: string | null): OpsUsageWindow {
  return raw === 'today' || raw === '30d' || raw === 'custom' ? raw : '7d';
}

export function CrmUsagePage(): JSX.Element {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const window = parseWindow(params.get('window'));
  const accountId = params.get('accountId') ?? undefined;
  const from = window === 'custom' ? params.get('from') ?? undefined : undefined;
  const to = window === 'custom' ? params.get('to') ?? undefined : undefined;
  const query: CrmUsageOverviewQuery = { window, from, to, accountId };
  const overview = useCrmUsageOverview(query);
  const pricing = usePricing();
  const cnyPerUsd = pricing.data?.policy?.cny_per_usd ?? null;

  function setWindow(next: OpsUsageWindow): void {
    const clone = new URLSearchParams(params);
    clone.set('window', next);
    if (next !== 'custom') {
      clone.delete('from');
      clone.delete('to');
    }
    setParams(clone, { replace: true });
  }

  function setCustomBound(key: 'from' | 'to', value: string): void {
    const clone = new URLSearchParams(params);
    clone.set('window', 'custom');
    if (value.length > 0) clone.set(key, value);
    else clone.delete(key);
    setParams(clone, { replace: true });
  }

  function clearAccountFilter(): void {
    const clone = new URLSearchParams(params);
    clone.delete('accountId');
    setParams(clone, { replace: true });
  }

  return (
    <section className="flex flex-col gap-8" data-testid="crm-usage-page">
      <PageHeader
        title={t('crm_usage.title')}
        sub={t('crm_usage.sub')}
        actions={
          <a
            href={crmUsageOverviewCsvUrl(query)}
            download="crm-usage-overview.csv"
            data-testid="crm-usage-csv-link"
            className={buttonClass('secondary', 'sm')}
          >
            <Download aria-hidden="true" className="h-3.5 w-3.5" />
            {t('crm_usage.export_csv')}
          </a>
        }
      />

      <div className="flex flex-wrap items-end gap-2" role="tablist" aria-label={t('crm_usage.window_aria')}>
        {(['today', '7d', '30d', 'custom'] as const).map((item) => (
          <Button
            key={item}
            variant="secondary"
            onClick={() => setWindow(item)}
            className={window === item ? 'bg-active text-paper' : undefined}
            data-testid={`crm-usage-window-${item}`}
          >
            {item === 'today' ? t('crm_usage.window.today') : item === 'custom' ? t('crm_usage.window.custom') : item}
          </Button>
        ))}
        <LocalDateTimeInput
          label={t('crm_usage.from_local')}
          value={params.get('from') ?? ''}
          onChange={(value) => setCustomBound('from', value)}
          inputClassName="w-56"
        />
        <LocalDateTimeInput
          label={t('crm_usage.to_local')}
          value={params.get('to') ?? ''}
          onChange={(value) => setCustomBound('to', value)}
          inputClassName="w-56"
        />
      </div>

      {accountId !== undefined ? (
        <div
          className="flex flex-wrap items-center gap-3 rounded-lg border border-line-soft bg-card px-4 py-2.5"
          data-testid="crm-usage-account-filter"
        >
          <span className="text-label text-paper-mid">{t('crm_usage.account_filter')}</span>
          <span className="font-mono text-dense text-paper">{accountId}</span>
          <Button variant="ghost" size="sm" onClick={clearAccountFilter} data-testid="crm-usage-clear-account">
            <X aria-hidden="true" className="h-3.5 w-3.5" />
            {t('crm_usage.clear_filter')}
          </Button>
        </div>
      ) : null}

      {overview.isLoading ? (
        <div className="rounded-lg border border-line-soft bg-card p-6 text-body text-paper-low" data-testid="crm-usage-loading">
          {t('crm_usage.loading')}
        </div>
      ) : overview.isError ? (
        <div className="rounded-lg border border-bad-line bg-bad-dim/50 p-6 text-body text-bad-bright" data-testid="crm-usage-error">
          {t('crm_usage.error', {
            message: overview.error instanceof Error ? overview.error.message : 'unknown error',
          })}
        </div>
      ) : overview.data ? (
        <UsageReady data={overview.data} cnyPerUsd={cnyPerUsd} t={t} />
      ) : null}
    </section>
  );
}

function UsageReady({
  data,
  cnyPerUsd,
  t,
}: {
  readonly data: CustomerManagerUsageOverviewResponse;
  readonly cnyPerUsd: number | null;
  readonly t: ReturnType<typeof useT>;
}): JSX.Element {
  const bookWide = data.scope.account_id === null;
  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label={t('crm_usage.metric.requests')}
          value={formatTokens(data.totals.request_count)}
          delta={{
            text: t('crm_usage.metric.requests_sub', {
              success: formatTokens(data.totals.success_count),
              error: formatTokens(data.totals.error_count),
            }),
            tone: 'neutral',
          }}
          testId="crm-usage-metric-requests"
        />
        <KpiCard
          label={t('crm_usage.metric.tokens')}
          value={formatTokens(data.totals.input_tokens + data.totals.output_tokens)}
          delta={{
            text: t('crm_usage.metric.tokens_sub', {
              input: formatTokens(data.totals.input_tokens),
              output: formatTokens(data.totals.output_tokens),
            }),
            tone: 'neutral',
          }}
          testId="crm-usage-metric-tokens"
        />
        <KpiCard
          label={t('crm_usage.metric.cache')}
          value={formatTokens(data.totals.cache_read_tokens + data.totals.cache_write_tokens)}
          delta={{
            text: t('crm_usage.metric.cache_sub', {
              read: formatTokens(data.totals.cache_read_tokens),
              write: formatTokens(data.totals.cache_write_tokens),
            }),
            tone: 'neutral',
          }}
          testId="crm-usage-metric-cache"
        />
        <KpiCard
          label={t('crm_usage.metric.charged')}
          value={formatUsdMicrosOrCentsAsCnyDisplay(
            data.totals.usd_charged_micros,
            data.totals.usd_charged_cents,
            cnyPerUsd, false)}
          delta={{
            text: t('crm_usage.metric.charged_sub', {
              usd: formatUsdMicrosOrCents(data.totals.usd_charged_micros, data.totals.usd_charged_cents, false),
            }),
            tone: 'neutral',
          }}
          testId="crm-usage-metric-charged"
        />
        <KpiCard
          label={t('crm_usage.metric.window')}
          value={`${formatLocalTimestamp(data.window.from)} → ${formatLocalTimestamp(data.window.to)}`}
          delta={{
            text: !bookWide
              ? t('crm_usage.metric.window_customer_sub')
              : data.scope.customer_count > 0
                ? t('crm_usage.metric.window_book_sub', { count: data.scope.customer_count })
                : // Admin sessions opting into the CRM view have no book;
                  // an empty sales book also lands here with zero rows.
                  t('crm_usage.metric.window_all_sub'),
            tone: 'neutral',
          }}
          className="md:col-span-2"
          testId="crm-usage-metric-window"
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <UsageBreakdownTable
          title={t('crm_usage.table.models')}
          empty={t('crm_usage.table.empty_models')}
          t={t}
        >
          {data.models.map((row) => (
            <UsageBreakdownRow
              key={`${row.vendor}:${row.model}`}
              primary={row.model}
              secondary={row.vendor}
              totals={row}
              cnyPerUsd={cnyPerUsd}
            />
          ))}
        </UsageBreakdownTable>
        <UsageBreakdownTable
          title={t('crm_usage.table.skus')}
          empty={t('crm_usage.table.empty_skus')}
          t={t}
        >
          {data.skus.map((row) => (
            <UsageBreakdownRow
              key={row.sku_id ?? 'legacy'}
              primary={row.sku_name ?? t('crm_usage.legacy_sku')}
              secondary={row.sku_id ?? '—'}
              totals={row}
              cnyPerUsd={cnyPerUsd}
            />
          ))}
        </UsageBreakdownTable>
      </div>
    </>
  );
}

function UsageBreakdownTable({
  title,
  empty,
  t,
  children,
}: {
  readonly title: string;
  readonly empty: string;
  readonly t: ReturnType<typeof useT>;
  readonly children: ReactNode;
}): JSX.Element {
  const count = Array.isArray(children) ? children.length : 1;
  return (
    <Panel caption={title}>
      {count === 0 ? (
        <p className="m-0 p-4 text-body text-paper-low">{empty}</p>
      ) : (
        <Table className="border-none">
          <THead>
            <Tr interactive={false}>
              <Th>{t('crm_usage.col.object')}</Th>
              <Th num>{t('crm_usage.col.requests')}</Th>
              <Th num>{t('crm_usage.col.io_tokens')}</Th>
              <Th num>{t('crm_usage.col.cache_tokens')}</Th>
              <Th num>{t('crm_usage.col.charged')}</Th>
            </Tr>
          </THead>
          <TBody>{children}</TBody>
        </Table>
      )}
    </Panel>
  );
}

function UsageBreakdownRow({
  primary,
  secondary,
  totals,
  cnyPerUsd,
}: {
  readonly primary: string;
  readonly secondary: string;
  readonly totals: CustomerManagerUsageTotals;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  return (
    <Tr>
      <Td>
        <div className="font-medium text-paper">{primary}</div>
        <div className="font-mono text-micro text-paper-low">{secondary}</div>
      </Td>
      <Td num>{formatTokens(totals.request_count)}</Td>
      <Td num>{formatTokenPair(totals.input_tokens, totals.output_tokens)}</Td>
      <Td num>{formatTokenPair(totals.cache_read_tokens, totals.cache_write_tokens)}</Td>
      <Td num>
        {formatUsdMicrosOrCentsAsCnyMetered(totals.usd_charged_micros, totals.usd_charged_cents, cnyPerUsd, false)}
      </Td>
    </Tr>
  );
}
